import React from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Pagination,
  Spinner,
  Table,
} from "react-bootstrap";
import { ConfirmModal } from "../modals/confirm-modal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ApiClient } from "../../utils/apiClient";
import {
  ArtworkWorkspaceArtworkDetail,
  ArtworkWorkspaceArtworkListItem,
  ArtworkWorkspaceConfig,
  ArtworkWorkspaceSort,
  ArtworkWorkspaceViewMode,
} from "../../types/artworkWorkspace";
import { AppState } from "../../store/root.reducer";
import User from "../../models/user.model";
import { WorkspaceStatusChip } from "./WorkspaceStatusChip";
import { WorkspaceMediaPreview } from "./WorkspaceMediaPreview";
import workspaceTheme from "./workspaceTheme.module.css";
import { trackWorkspaceEvent } from "../../utils/workspaceTelemetry";
import { getNormalizedUserRoles, hasEdmCuratorCapability } from "../../utils/userAccess";

const BASE_API = process.env.REACT_APP_BASE_API || "";
const apiClient = ApiClient(BASE_API);
const PAGE_SIZE = 24;

type Props = {
  config: ArtworkWorkspaceConfig;
};

const sortOptions: Array<{ value: ArtworkWorkspaceSort; label: string }> = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "title", label: "Title" },
  { value: "artist", label: "Artist" },
  { value: "type", label: "Type" },
];

type PaginationToken = number | "start-ellipsis" | "end-ellipsis";

const buildPaginationTokens = (currentPage: number, totalPages: number): PaginationToken[] => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const tokens: PaginationToken[] = [1];
  let middleStart = Math.max(2, currentPage - 1);
  let middleEnd = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 3) {
    middleStart = 2;
    middleEnd = 4;
  } else if (currentPage >= totalPages - 2) {
    middleStart = totalPages - 3;
    middleEnd = totalPages - 1;
  }

  if (middleStart > 2) {
    tokens.push("start-ellipsis");
  }

  for (let pageNumber = middleStart; pageNumber <= middleEnd; pageNumber += 1) {
    tokens.push(pageNumber);
  }

  if (middleEnd < totalPages - 1) {
    tokens.push("end-ellipsis");
  }

  tokens.push(totalPages);
  return tokens;
};

const getSourceLine = (item: ArtworkWorkspaceArtworkListItem | ArtworkWorkspaceArtworkDetail) =>
  [item.provider, item.dataProvider].filter(Boolean).join(" / ") || "Internal";

const formatWorkspaceTimestamp = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return new Date(parsed).toLocaleString();
};

const formatWorkspaceCompactTimestamp = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return new Date(parsed).toLocaleString(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  });
};

export const ArtworksWorkspaceTab = ({ config }: Props) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector((state: AppState) => state.auth.user as User | null);
  const roles = getNormalizedUserRoles(user);
  const isAdminWorkspaceUser = roles.includes("GALLERY") || roles.includes("ADMIN") || roles.includes("SUPERADMIN");
  const curatorAccess = hasEdmCuratorCapability(user);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<ArtworkWorkspaceArtworkListItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [activeArtwork, setActiveArtwork] = React.useState<ArtworkWorkspaceArtworkDetail | null>(null);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [sheetLoading, setSheetLoading] = React.useState(false);
  const [actionMessage, setActionMessage] = React.useState<string | null>(null);
  const [actionMessageVariant, setActionMessageVariant] = React.useState<"info" | "success" | "danger">("info");
  const [deleteTarget, setDeleteTarget] = React.useState<ArtworkWorkspaceArtworkListItem | null>(null);
  const [deletingArtworkId, setDeletingArtworkId] = React.useState<string | null>(null);
  const [searchDraft, setSearchDraft] = React.useState(searchParams.get("artSearch") || "");
  const requestRef = React.useRef(0);
  const detailRequestRef = React.useRef(0);
  const resultsRef = React.useRef<HTMLDivElement | null>(null);

  const viewMode = ((searchParams.get("artView") || "cards") as ArtworkWorkspaceViewMode);
  const sort = ((searchParams.get("artSort") || "newest") as ArtworkWorkspaceSort);
  const sortBy = searchParams.get("artSortBy") || "";
  const sortDirection = searchParams.get("artSortDirection") === "desc" ? "desc" : "asc";
  const page = Math.max(1, Number(searchParams.get("artPage") || "1"));
  const search = searchParams.get("artSearch") || "";
  const availableSortOptions = React.useMemo(
    () => (
      curatorAccess
        ? [...sortOptions, { value: "institution" as ArtworkWorkspaceSort, label: "Institution" }]
        : sortOptions
    ),
    [curatorAccess]
  );

  const renderSafeSortIndicator = (state: "asc" | "desc" | null) => {
    if (state === "asc") {
      return <span className={workspaceTheme.tableSortIndicator} aria-hidden="true">&uarr;</span>;
    }
    if (state === "desc") {
      return <span className={workspaceTheme.tableSortIndicator} aria-hidden="true">&darr;</span>;
    }
    return <span className={workspaceTheme.tableSortIndicatorPlaceholder} aria-hidden="true">&uarr;</span>;
  };

  React.useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  React.useEffect(() => {
    if ((sort !== "institution" && sortBy !== "institutionName") || curatorAccess) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.set("artSort", "newest");
    next.delete("artSortBy");
    next.delete("artSortDirection");
    next.set("artPage", "1");
    setSearchParams(next, { replace: true });
  }, [curatorAccess, searchParams, setSearchParams, sort, sortBy]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalizedDraft = searchDraft.trim();
      if (normalizedDraft === search) {
        return;
      }
      const next = new URLSearchParams(searchParams);
      if (normalizedDraft) {
        next.set("artSearch", normalizedDraft);
      } else {
        next.delete("artSearch");
      }
      next.set("artPage", "1");
      setSearchParams(next, { replace: true });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [searchDraft, searchParams, setSearchParams]);

  const setParam = React.useCallback((key: string, value?: string) => {
    const next = new URLSearchParams(searchParams);
    if (value == null || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const scrollResultsIntoView = React.useCallback(() => {
    resultsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const handlePageChange = React.useCallback((nextPage: number) => {
    scrollResultsIntoView();
    setParam("artPage", String(nextPage));
  }, [scrollResultsIntoView, setParam]);

  const loadItems = React.useCallback(async () => {
    const currentRequest = requestRef.current + 1;
    requestRef.current = currentRequest;
    setLoading(true);
    setError(null);
    const response = await apiClient.getArtworkWorkspaceArtworks({
      page,
      limit: PAGE_SIZE,
      search: search || undefined,
      sort: sortBy ? undefined : sort,
      sortBy: sortBy || undefined,
      sortDirection: sortBy ? sortDirection : undefined,
      institutionId: isAdminWorkspaceUser ? undefined : config.defaultInstitutionId || undefined,
    });

    if (requestRef.current !== currentRequest) {
      return;
    }

    if (!response.status) {
      setError(response.error?.message || "Failed to load artworks.");
      trackWorkspaceEvent("artworks_list_failed", { message: response.error?.message || "Failed to load artworks." });
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    setItems(response.data.items || []);
    setTotal(response.data.total || 0);
    setLoading(false);
    trackWorkspaceEvent("artworks_list_loaded", {
      total: response.data.total || 0,
      page,
      sort,
      search,
    });
  }, [config.defaultInstitutionId, isAdminWorkspaceUser, page, search, sort, sortBy, sortDirection]);

  const setSort = React.useCallback((nextSort: ArtworkWorkspaceSort) => {
    const next = new URLSearchParams(searchParams);
    next.set("artSort", nextSort);
    next.delete("artSortBy");
    next.delete("artSortDirection");
    next.set("artPage", "1");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const getHeaderSortState = React.useCallback((sortValue: ArtworkWorkspaceSort, sortByValue?: string) => {
    if (sortByValue) {
      if (sortBy !== sortByValue) {
        return null;
      }
      return sortDirection;
    }

    if (sortValue === "newest") {
      return !sortBy && sort === "newest" ? "desc" : null;
    }
    if (sortValue === "oldest") {
      return !sortBy && sort === "oldest" ? "asc" : null;
    }

    return !sortBy && sort === sortValue ? "asc" : null;
  }, [sort, sortBy, sortDirection]);

  const setDirectionalSort = React.useCallback((sortValue: ArtworkWorkspaceSort, sortByValue: string) => {
    const currentDirection = getHeaderSortState(sortValue, sortByValue);
    const nextDirection = currentDirection === "asc" ? "desc" : "asc";
    const next = new URLSearchParams(searchParams);
    next.set("artSort", sortValue);
    next.set("artSortBy", sortByValue);
    next.set("artSortDirection", nextDirection);
    next.set("artPage", "1");
    setSearchParams(next, { replace: true });
  }, [getHeaderSortState, searchParams, setSearchParams]);

  const toggleCreatedSort = React.useCallback(() => {
    const next = new URLSearchParams(searchParams);
    const nextSort = sort === "oldest" && !sortBy ? "newest" : "oldest";
    next.set("artSort", nextSort);
    next.delete("artSortBy");
    next.delete("artSortDirection");
    next.set("artPage", "1");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, sort, sortBy]);

  const renderSortIndicator = (state: "asc" | "desc" | null) => {
    if (state === "asc") {
      return <span className={workspaceTheme.tableSortIndicator} aria-hidden="true">↑</span>;
    }
    if (state === "desc") {
      return <span className={workspaceTheme.tableSortIndicator} aria-hidden="true">↓</span>;
    }
    return <span className={workspaceTheme.tableSortIndicatorPlaceholder} aria-hidden="true">↑</span>;
  };

  React.useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const openArtwork = async (artworkId: string) => {
    if (window.innerWidth < 992) {
      trackWorkspaceEvent("artwork_detail_navigate_mobile", { artworkId });
      navigate(`/artwork/list/${artworkId}`);
      return;
    }

    const currentDetailRequest = detailRequestRef.current + 1;
    detailRequestRef.current = currentDetailRequest;
    setSheetOpen(true);
    setActiveArtwork(null);
    setSheetLoading(true);
    const response = await apiClient.getArtworkWorkspaceArtwork(artworkId);
    if (detailRequestRef.current !== currentDetailRequest) {
      return;
    }

    setSheetLoading(false);
    if (!response.status) {
      setSheetOpen(false);
      setActiveArtwork(null);
      setActionMessageVariant("danger");
      setActionMessage(response.error?.message || "Failed to load artwork.");
      trackWorkspaceEvent("artwork_sheet_failed", { artworkId, message: response.error?.message || "Failed to load artwork." });
      return;
    }

    setActiveArtwork(response.data);
    trackWorkspaceEvent("artwork_sheet_opened", { artworkId });
  };

  const closeArtworkSheet = React.useCallback(() => {
    detailRequestRef.current += 1;
    setSheetOpen(false);
    setActiveArtwork(null);
    setSheetLoading(false);
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const paginationTokens = React.useMemo(
    () => buildPaginationTokens(page, totalPages),
    [page, totalPages]
  );
  const closeDeleteConfirm = React.useCallback(() => {
    if (deletingArtworkId) {
      return;
    }

    setDeleteTarget(null);
  }, [deletingArtworkId]);
  const handleDeleteArtwork = React.useCallback(async () => {
    if (!deleteTarget || deletingArtworkId) {
      return;
    }

    setDeletingArtworkId(deleteTarget.artworkId);
    const response = await apiClient.deleteArtworkWorkspaceArtwork(deleteTarget.artworkId);
    setDeletingArtworkId(null);

    if (!response.status) {
      setActionMessageVariant("danger");
      setActionMessage(response.error?.message || "Failed to delete artwork.");
      return;
    }

    const deletedArtworkId = deleteTarget.artworkId;
    const deletedArtworkTitle = deleteTarget.title || "Artwork";

    if (page > 1 && items.length === 1) {
      setParam("artPage", String(page - 1));
    } else {
      setItems((current) => current.filter((item) => item.artworkId !== deletedArtworkId));
    }
    setTotal((current) => Math.max(0, current - 1));
    if (activeArtwork?.artworkId === deletedArtworkId) {
      setSheetOpen(false);
      setActiveArtwork(null);
    }
    setDeleteTarget(null);
    setActionMessageVariant("success");
    setActionMessage(`Deleted "${deletedArtworkTitle}".`);
    trackWorkspaceEvent("artwork_deleted", { artworkId: deletedArtworkId });
  }, [activeArtwork?.artworkId, deleteTarget, deletingArtworkId, items.length, page, setParam]);

  return (
    <div className={`d-flex flex-column gap-3 ${workspaceTheme.sectionSurface}`}>
      <div className={workspaceTheme.toolbar}>
        <div className={workspaceTheme.toolbarGroup}>
          <Form.Group controlId="artwork-workspace-search" style={{ minWidth: 280 }}>
            <Form.Label>Search</Form.Label>
            <Form.Control
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder="Search title, creator, provider"
            />
          </Form.Group>
          <Form.Group controlId="artwork-workspace-sort" style={{ minWidth: 180 }}>
            <Form.Label>Sort</Form.Label>
            <Form.Select className={workspaceTheme.workspaceSelect} value={sort} onChange={(event) => setSort(event.target.value as ArtworkWorkspaceSort)}>
              {availableSortOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
        <div className={workspaceTheme.workspaceToggleGroup} role="group" aria-label="Artwork view mode">
          <button
            type="button"
            className={`${workspaceTheme.workspaceToggleButton} ${viewMode === "cards" ? workspaceTheme.workspaceToggleButtonActive : workspaceTheme.workspaceToggleButtonInactive}`}
            aria-pressed={viewMode === "cards"}
            onClick={() => setParam("artView", "cards")}
          >
            Cards
          </button>
          <button
            type="button"
            className={`${workspaceTheme.workspaceToggleButton} ${viewMode === "list" ? workspaceTheme.workspaceToggleButtonActive : workspaceTheme.workspaceToggleButtonInactive}`}
            aria-pressed={viewMode === "list"}
            onClick={() => setParam("artView", "list")}
          >
            List
          </button>
        </div>
      </div>

      {actionMessage ? (
        <Alert
          variant={actionMessageVariant}
          className={workspaceTheme.surfaceAlert}
          onClose={() => setActionMessage(null)}
          dismissible
        >
          {actionMessage}
        </Alert>
      ) : null}

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <div ref={resultsRef}>
        {loading ? (
          <div className="py-5 text-center"><Spinner animation="border" /></div>
        ) : items.length === 0 ? (
          <div className={workspaceTheme.emptyState}>No artworks match the current workspace filters.</div>
        ) : viewMode === "cards" ? (
          <div className="row g-3">
            {items.map((item) => (
              <div className="col-md-6 col-xl-4" key={item.artworkId}>
                <Card
                  className={`h-100 ${workspaceTheme.darkCard} ${workspaceTheme.clickableCard}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => void openArtwork(item.artworkId)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      void openArtwork(item.artworkId);
                    }
                  }}
                >
                  <div className="p-3">
                    <WorkspaceMediaPreview
                      type={item.type}
                      title={item.title}
                      previewUrl={item.previewUrl}
                      runtimeMedia={item.runtimeMedia}
                      preferStaticPoster
                      variant="card"
                    />
                  </div>
                  <Card.Body className="d-flex flex-column gap-3">
                    <div className={workspaceTheme.cardActionRow}>
                      <div className={workspaceTheme.chipRow}>
                        <WorkspaceStatusChip label={item.recordState} />
                        <WorkspaceStatusChip label={item.type || "Artwork"} tone="neutral" />
                      </div>
                      <div className={workspaceTheme.cardActionGroup}>
                        <Button
                          variant="light"
                          size="sm"
                          className={`${workspaceTheme.cardActionButton} ${workspaceTheme.cardPrimaryAction} ${workspaceTheme.workspaceButtonSmall}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/artwork/list/${item.artworkId}`);
                          }}
                        >
                          Open
                        </Button>
                        {item.canDelete ? (
                          <Button
                            variant="light"
                            size="sm"
                            className={`${workspaceTheme.cardActionButton} ${workspaceTheme.cardDangerAction} ${workspaceTheme.workspaceButtonSmall}`}
                            disabled={deletingArtworkId === item.artworkId}
                            onClick={(event) => {
                              event.stopPropagation();
                              setDeleteTarget(item);
                            }}
                          >
                            {deletingArtworkId === item.artworkId ? "Deleting..." : "Delete"}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    <div>
                      <div className={workspaceTheme.cardTitle}>{item.title || "Untitled artwork"}</div>
                      <div className={workspaceTheme.neutralText}>{item.artistName || "Unknown creator"}</div>
                    </div>
                    <div className={workspaceTheme.cardMetaGrid}>
                      <div className={workspaceTheme.metaCell}>
                        <span className={`${workspaceTheme.metaLabel} me-2`}>ID:</span>
                        <span>{item.artworkId}</span>
                      </div>
                      <div className={workspaceTheme.metaCell}>
                        <span className={`${workspaceTheme.metaLabel} me-2`}>Created at:</span>
                        <span>{formatWorkspaceTimestamp(item.createdAt) || "Unknown"}</span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <Table responsive hover variant="dark" className={workspaceTheme.darkTable}>
            <thead>
              <tr>
                <th>Preview</th>
                <th>
                  <button
                    type="button"
                    className={workspaceTheme.tableSortButton}
                    onClick={() => setDirectionalSort("title", "title")}
                  >
                    <span>Artwork</span>
                    {renderSafeSortIndicator(getHeaderSortState("title", "title"))}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className={workspaceTheme.tableSortButton}
                    onClick={() => setDirectionalSort("type", "type")}
                  >
                    <span>Type</span>
                    {renderSafeSortIndicator(getHeaderSortState("type", "type"))}
                  </button>
                </th>
                {isAdminWorkspaceUser ? (
                  <th>
                    <button
                      type="button"
                      className={workspaceTheme.tableSortButton}
                      onClick={() => setDirectionalSort("institution", "institutionName")}
                    >
                      <span>Institution</span>
                      {renderSafeSortIndicator(getHeaderSortState("institution", "institutionName"))}
                    </button>
                  </th>
                ) : null}
                <th>ID</th>
                <th>
                  <button
                    type="button"
                    className={workspaceTheme.tableSortButton}
                    onClick={toggleCreatedSort}
                  >
                    <span>Created at</span>
                    {renderSafeSortIndicator(getHeaderSortState("newest"))}
                  </button>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.artworkId}>
                  <td className={workspaceTheme.previewCell}>
                    <div className={workspaceTheme.tablePreview}>
                      <WorkspaceMediaPreview
                        type={item.type}
                        title={item.title}
                        previewUrl={item.previewUrl}
                        runtimeMedia={item.runtimeMedia}
                        preferStaticPoster
                        variant="list"
                      />
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`btn btn-link p-0 text-decoration-none ${workspaceTheme.tableAction}`}
                      onClick={() => void openArtwork(item.artworkId)}
                    >
                      {item.title || "Untitled artwork"}
                    </button>
                    <div className={workspaceTheme.secondaryText}>{item.artistName || "Unknown creator"}</div>
                  </td>
                  <td>{item.type || "Unknown"}</td>
                  {isAdminWorkspaceUser ? <td>{item.institution?.name || "-"}</td> : null}
                  <td className={workspaceTheme.tableIdCell}>{item.artworkId}</td>
                  <td className={workspaceTheme.tableTimestampCell}>{formatWorkspaceCompactTimestamp(item.createdAt) || "Unknown"}</td>
                  <td className={workspaceTheme.actionCell}>
                    <div className={workspaceTheme.inlineActionRow}>
                      <Button
                        variant="light"
                        size="sm"
                        className={`${workspaceTheme.cardActionButton} ${workspaceTheme.cardPrimaryAction} ${workspaceTheme.workspaceButtonSmall}`}
                        onClick={() => navigate(`/artwork/list/${item.artworkId}`)}
                      >
                        Open
                      </Button>
                      {item.canDelete ? (
                        <Button
                          variant="light"
                          size="sm"
                          className={`${workspaceTheme.cardActionButton} ${workspaceTheme.cardDangerAction} ${workspaceTheme.workspaceButtonSmall}`}
                          disabled={deletingArtworkId === item.artworkId}
                          onClick={() => setDeleteTarget(item)}
                        >
                          {deletingArtworkId === item.artworkId ? "Deleting..." : "Delete"}
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {totalPages > 1 ? (
        <div className={workspaceTheme.workspacePagination}>
          <Pagination className="mb-0">
            <Pagination.Item disabled={page <= 1} onClick={page <= 1 ? undefined : () => handlePageChange(page - 1)}>
              Prev
            </Pagination.Item>
            {paginationTokens.map((token) => {
              if (token === "start-ellipsis" || token === "end-ellipsis") {
                return <Pagination.Ellipsis key={token} disabled />;
              }

              return (
                <Pagination.Item
                  key={token}
                  active={token === page}
                  onClick={token === page ? undefined : () => handlePageChange(token)}
                >
                  {token}
                </Pagination.Item>
              );
            })}
            <Pagination.Item disabled={page >= totalPages} onClick={page >= totalPages ? undefined : () => handlePageChange(page + 1)}>
              Next
            </Pagination.Item>
          </Pagination>
        </div>
      ) : null}

      {sheetOpen ? (
        <>
          <div className={workspaceTheme.sheetBackdrop} onClick={closeArtworkSheet} />
          <aside className={workspaceTheme.sheetPanel} aria-label="Artwork quick inspect sheet">
            <div className={workspaceTheme.sheetHeader}>
              <div className={workspaceTheme.sheetHeaderText}>
                <div className={workspaceTheme.cardTitle}>{activeArtwork?.title || "Artwork"}</div>
                <div className={workspaceTheme.secondaryText}>{activeArtwork?.artistName || "Unknown creator"}</div>
              </div>
              <Button
                variant="light"
                size="sm"
                className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                onClick={closeArtworkSheet}>
                Close
              </Button>
            </div>
            <div className={workspaceTheme.sheetBody}>
              {sheetLoading && !activeArtwork ? <Spinner animation="border" /> : null}
              {activeArtwork ? (
              <div className={workspaceTheme.sheetSection}>
                <WorkspaceMediaPreview
                  type={activeArtwork.artwork.type}
                  title={activeArtwork.title}
                  previewUrl={activeArtwork.artwork.media.thumbnailUrl}
                  dataUrl={activeArtwork.artwork.media.dataUrl}
                  originalUrl={activeArtwork.artwork.media.originalUrl}
                  runtimeMedia={activeArtwork.artwork.media.runtimeMedia}
                  variant="sheet"
                />
                <div className={workspaceTheme.chipRow}>
                  <WorkspaceStatusChip label={activeArtwork.recordState} />
                  <WorkspaceStatusChip label={activeArtwork.artwork.type || "Artwork"} tone="neutral" />
                </div>
                <div className={workspaceTheme.sheetBlock}>
                  <div className={workspaceTheme.sheetLabel}>Artwork ID</div>
                  <div className={workspaceTheme.sheetValue}>{activeArtwork.artwork.artworkId}</div>
                </div>
                <div className={workspaceTheme.sheetBlock}>
                  <div className={workspaceTheme.sheetLabel}>Artwork Type</div>
                  <div className={workspaceTheme.sheetValue}>{activeArtwork.artwork.type || "Artwork"}</div>
                </div>
                <div className={workspaceTheme.sheetBlock}>
                  <div className={workspaceTheme.sheetLabel}>Listed</div>
                  <div className={workspaceTheme.sheetValue}>{activeArtwork.listed ? "Listed" : "Not listed"}</div>
                </div>
                <div className={workspaceTheme.sheetBlock}>
                  <div className={workspaceTheme.sheetLabel}>Owner</div>
                  <div className={workspaceTheme.sheetValue}>{activeArtwork.artwork.owner?.name || activeArtwork.artwork.owner?.email || "Unknown owner"}</div>
                </div>
                <div className={workspaceTheme.sheetBlock}>
                  <div className={workspaceTheme.sheetLabel}>Created</div>
                  <div className={workspaceTheme.sheetValue}>{formatWorkspaceTimestamp(activeArtwork.createdAt) || "N/A"}</div>
                </div>
                <div className={workspaceTheme.sheetBlock}>
                  <div className={workspaceTheme.sheetLabel}>Updated</div>
                  <div className={workspaceTheme.sheetValue}>{formatWorkspaceTimestamp(activeArtwork.updatedAt) || "N/A"}</div>
                </div>
                <div className={workspaceTheme.sheetBlock}>
                  <div className={workspaceTheme.sheetLabel}>Description</div>
                  <div className={workspaceTheme.descriptionPreview}>
                    {activeArtwork.artwork.description?.trim() || "No description added yet."}
                  </div>
                </div>
                {curatorAccess ? (
                  <>
                    <div className={workspaceTheme.sheetBlock}>
                      <div className={workspaceTheme.sheetLabel}>Institution</div>
                      <div className={workspaceTheme.sheetValue}>{activeArtwork.institution?.name || "No institution"}</div>
                    </div>
                    <div className={workspaceTheme.sheetBlock}>
                      <div className={workspaceTheme.sheetLabel}>Dataset</div>
                      <div className={workspaceTheme.sheetValue}>{activeArtwork.datasetDisplayName || activeArtwork.datasetKey || "No dataset"}</div>
                    </div>
                    <div className={workspaceTheme.sheetBlock}>
                      <div className={workspaceTheme.sheetLabel}>Source</div>
                      <div className={workspaceTheme.sheetValue}>{getSourceLine(activeArtwork)}</div>
                    </div>
                    <div className={workspaceTheme.sheetBlock}>
                      <div className={workspaceTheme.sheetLabel}>Blockers</div>
                      <div className={workspaceTheme.sheetValue}>{activeArtwork.blockerCount > 0 ? `${activeArtwork.blockerCount} blockers` : "No blockers"}</div>
                    </div>
                  </>
                ) : null}
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    variant="light"
                    className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`}
                    onClick={() => navigate(`/artwork/list/${activeArtwork.artworkId}`)}
                  >
                    Open Full Details
                  </Button>
                </div>
              </div>
              ) : null}
            </div>
          </aside>
        </>
      ) : null}
      <ConfirmModal
        show={Boolean(deleteTarget)}
        onHide={closeDeleteConfirm}
        onConfirm={handleDeleteArtwork}
        title="Delete artwork"
        message={deleteTarget ? `Delete "${deleteTarget.title || "this artwork"}" permanently? This action cannot be undone.` : "Delete this artwork permanently? This action cannot be undone."}
        confirmText={deletingArtworkId ? "Deleting..." : "Delete"}
        cancelText="Cancel"
      />
    </div>
  );
};
